/**
 * Outcome components
 * @modules
 */
import { Variable, VArray, all, if as _if, not, or, and, Table, TR, TH, TD } from 'alkali'
import { Div, Button, ElementProperties, Label, Span } from 'alkali'
import * as _ from 'lodash'
import { PLUS_MINUS } from 'util/QuantifierParser'
import { FocusedState } from 'components/extract/ExtractAnnotationModel'
import { VRow } from 'components/extract/model/extraction'
import StudySummaryModel from 'components/extract/model/StudySummaryModel'
import { OutcomeExtractField, TimepointExtractField, UnitOfMeasureField } from 'components/extract/Inputs'
import Icon from 'components/Icon'
import * as positiveIcon from 'icons/monitor-heart-rate-up.svg'
import * as negativeIcon from 'icons/monitor-heart-rate-down.svg'
import * as settingsIcon from 'icons/cog.svg'
import * as infoIcon from 'icons/information-circle.svg'
import * as noteIcon from 'icons/notes-paper-text.svg'
import { FieldType, RowType } from 'services/extract/types'

import { compState, OutcomeStatus, DevTypeToggle, BinaryPctToggle, RowTypeButton, Mode, toRowConfigLabel } from './RowComponents'
import { TEXT_INPUT_CHANGE_EVENTS } from 'components/generic/TextInputControl'
import TextAreaControl from 'components/generic/TextAreaControl'

interface OutcomeComponentOpts extends ElementProperties<OutcomeComponent> {
	summary: StudySummaryModel
	outcome: VRow
	outcomes: VArray<VRow>
	allOutcomes?: any
	addValidation?: Variable<any>
	focusedState?: FocusedState
	extracting?: boolean
	mode: any
}

export class DirectionIcon extends Icon('.direction-icon') {
	created(props) {
		props.icon = _if(props.isPositive, [positiveIcon, negativeIcon])
		props.title = _if(props.isPositive, ['Positive', 'Negative'])
		super.created(props)
	}
}

class OutcomeDirectionButton extends Button('.outcome-direction') {
	created(props) {
		const { isPositive, directionSet, configurable, outcome } = props
		props.classes = {
			// if not specified, fade icon
			'unspecified': isPositive.to(pos => pos == null),
			'unset': not(directionSet)
		}
		Object.assign(props, {
			title: all([isPositive, directionSet], ((pos, updated) =>
					(pos == null ? 'Unspecified (assume negative)' : pos == true ? 'Positive' : 'Negative') + (updated ? ' (*updated)' : '')
			)),
			disabled: not(configurable),
			onclick() {
				const pos = isPositive.valueOf()
				const next = pos == null ? true : pos == true ? false : null
				isPositive.put(next)
				outcome.property('isPositive').put(next)
			},
			content: [
				new DirectionIcon({ isPositive })
			]
		})
	}
}

class OptionsPanel extends Table('.row-options') {
	created(props) {
		const row = props.row
		const rowTypeTitle = props.rowTypeTitle || 'Outcome'
		props.content = [
			TR('.row-def', [
				TH([Label(rowTypeTitle + ' note:',)]),
				TD(props.configurable.to(c => [
					c ? TextAreaControl('.row-def-input', {
						inputEvents: _.without(TEXT_INPUT_CHANGE_EVENTS, 'keyup', 'click', 'input'),
						rows: 1
					}, row.definition) : Div({
						classes: { unset: not(row.definition) }
					}, row.definition.to(d => d ? d : 'blank'))
				]))
			]),
			TR('.row-method', { classes: { hidden: not(row.method ) } }, [
				TH([Label('Method:')]),
				TD([Div(row.method)])
			])
		]
	}
}

export default class OutcomeComponent extends Div {
	private unitDefaultUpdater
	private uom: UnitOfMeasureField
	statusIcon
	devTypeToggle
	binaryPctToggle
	timepoint: [typeof Div, TimepointExtractField]
	optionsVisible: Variable<boolean>
	optionsPanel: OptionsPanel

	created(props: OutcomeComponentOpts) {
		const { summary, mode, outcome, outcomes, allOutcomes, addValidation = new Variable(Object.assign([], { isValid: true })), focusedState, extracting } = props

		const type = outcome.property('type')
		const ctns = type.to(t => t as unknown as FieldType == FieldType.CONTINUOUS)
		const binary = type.to(t => t as unknown as FieldType == FieldType.BINARY)
		const rowConfigLabel = toRowConfigLabel(outcome)
		const origPositive = outcome.property('initIsPositive')
		const isPositive = new Variable(origPositive.valueOf())
		const directionSet = outcome.property('isPositive').to(p => p !== undefined)
		this.unitDefaultUpdater = {
			updated(e, variable) {
				const name = variable.valueOf()
				if (/\bscore\b|\bscale\b/i.test(name?.term?.label)) {
					outcome.unit.put('units')
				}
			}
		}
		outcome.property('name').notifies(this.unitDefaultUpdater)
		const isPctChange = outcome.rowType.to(r => r == RowType.PCT_CHANGE)
		const unitLabel = isPctChange.to(b => b ? '%' : outcome.unit)
		const state = all([mode, outcome, outcomes], (mode, outcome, outcomes) =>
			compState(mode, outcome, outcomes, summary.studyCtx)
		)
		const configurable = state.property('configurable')
		const disabled = not(configurable)
		const timePointDisabled = all([disabled, state], (disabled, state) =>
			disabled || (state.mode == 'edit' && !state.comparison.editable.isRE)
		)
		this.statusIcon = new OutcomeStatus({ outcome, state, addValidation })
		this.devTypeToggle = DevTypeToggle(outcome, disabled)
		this.binaryPctToggle = BinaryPctToggle(outcome, disabled)
		const binaryPctLabel = this.binaryPctToggle.isBinaryPct.to(b => b ? '%' : '#')
		const editableShown = { classes: { hidden: not(configurable) } }
		const roShown = { classes: { hidden: configurable } }
		this.timepoint = [
			Div('.timepoint.ro', { title: outcome.timepoint.val, classes: { hidden: not(timePointDisabled) } }, outcome.timepoint.val),
			new TimepointExtractField('.timepoint', {
				ftype: 'timepoint',
				disabled: timePointDisabled,
				optional: true,
				obj: outcome.property('timepoint'),
				focusedState,
				classes: { hidden: timePointDisabled }
			})
		]

		const typeEditable = and(configurable, not(outcome.existing))
		const optionsVisible = this.optionsVisible = new Variable(false)

		this.optionsPanel = new OptionsPanel({ classes: { hidden: not(optionsVisible) }, row: outcome, configurable })

		props.classes = {
			ro: not(configurable)
		}

		props.content = [
			Div('.outcome-component', editableShown, [
				Div('.icon-wrap', [
					Button('.settings-toggle', {
						title: 'Additional options...',
						type: 'button',
						classes: { active: this.optionsVisible },
						onclick() {
							optionsVisible.put(!optionsVisible.valueOf())
						}
					}, [Icon({ icon: noteIcon, classes: { disabled: not(configurable) } })])
				]),
				Div('.icon-wrap.outcome-icon', [
					new OutcomeDirectionButton({
						outcome,
						configurable,
						isPositive,
						directionSet
					})
				]),
				Div('.field-wrap', [
					new OutcomeExtractField('.outcome-term', {
						outcome,
						allOutcomes,
						isPositive,
						focusedState,
						configurable
					})
				]),
				Div('.icon-wrap.binary-num', { classes: { hidden: not(binary) } },
					Button('.binary-num-btn', {
						classes: { active: not(this.binaryPctToggle.isBinaryPct) },
						disabled: not(typeEditable),
						onclick: this.binaryPctToggle.binaryPctNumToggle(false)
					}, Div(['#']))
				),
				Div('.icon-wrap.binary-pct', { classes: { hidden: not(binary) } },
					Button('.binary-pct-btn', {
						classes: { active: this.binaryPctToggle.isBinaryPct },
						disabled: not(typeEditable),
						onclick: this.binaryPctToggle.binaryPctNumToggle(true)
					}, Div(['%']))
				),
				Div('.icon-wrap.outcome-uom', { classes: { hidden: not(ctns) } }, ctns.to(ctns => ctns ? [
					this.uom = new UnitOfMeasureField('.center-component', {
						disabled: or(not(typeEditable), isPctChange)
					}, unitLabel)
				] : [])),
				Div('.icon-wrap', { classes: { hidden: !extracting || not(ctns) } }, ctns.to(ctns => ctns ? [
					Button('.dev-type', {
						title: 'Standard Deviation/Error',
						type: 'button',
						disabled: not(typeEditable),
						onclick: this.devTypeToggle.devTypeToggle,
					}, this.devTypeToggle.devTypeLabel.to(t => PLUS_MINUS + t))
				] : [])),
				Div('.icon-wrap', [
					new RowTypeButton({
						title: 'Outcome Type',
						row: outcome,
						editable: typeEditable
					})
				])
			]),
			Div('.outcome-component.ro', roShown, [
				// Div('.icon-wrap.outcome-icon', [new DirectionIcon({ isPositive, directionSet })]),
				// Div('.field-wrap', outcome.name.val),
				// Div('.icon-wrap', { classes: { hidden: ctns } }, this.binaryPctToggle.isBinaryPct.to(b => b ? '%' : '#')),
				// Div('.icon-wrap.outcome-uom', { classes: { hidden: not(ctns) } }, isPctChange.to(b => b ? '%' : outcome.unit)),
				// Div('.icon-wrap', { classes: { hidden: !extracting || not(ctns) } }, this.devTypeToggle.devTypeLabel.to(t => PLUS_MINUS + t)),
				// Div('.icon-wrap', toRowConfigLabel(outcome)),
				Div('.field-wrap', [
					Div('.row-dir', [
						new DirectionIcon({ isPositive }),
						Span('.sep', '|')
					]),
					Div('.row-name', { title: 'Name: ' + outcome.name.val }, [
						outcome.name.val
					]),
					Div('.row-pct-num', {
						title: binaryPctLabel,
						classes: { hidden: not(binary) }
					}, [
						Span('.sep', '|'),
						binaryPctLabel
					]),
					Div('.row-unit', { title: unitLabel.to(l => `Unit: ${l}`), classes: { hidden: not(ctns) } }, [
						Span('.sep', '|'),
						unitLabel
					]),
					//Span({ classes: { hidden: !extracting || not(ctns) } }, this.devTypeToggle.devTypeLabel.to(t => PLUS_MINUS + t)),
					Div('.row-type', { title: rowConfigLabel.to(l => `Type: ${l}`) }, [
						Span('.paren', '('),
						rowConfigLabel,
						Span('.paren', ')')
					])
				])
			])
		]
	}

	private detached() {
		this.outcome.property('name').stopNotifies(this.unitDefaultUpdater)
		this.unitDefaultUpdater = null
	}

	setUnit(unit) {
		this.uom.setValue(unit)
	}
}
